import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  Modal,
  TextField,
  InputAdornment,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { useCallback, useState } from "react";
import useResponsive from "../../hooks/useResponsive";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import { useTheme } from "@mui/material/styles";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { headerSty, contentSty } from "../table-styles";
import { usePaystackPayment } from "react-paystack";
import Axios from "../../utils/http-request";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../notification";
import { useWalletSettings } from "../../hooks/useWalletSetting";
import { useQuery } from "@tanstack/react-query";
import { formatDateAndTimeFromString } from "../../utils/url-id";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import { Pagination } from "../Pagination";

const WalletContent = () => {
  const isMedium = useResponsive("down", "md", "xs");
  const [openModal, setOpenModal] = useState(false);
  // const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationState, setPaginationState] = useState<PaginationDetails>(
    PaginationDetailsDummyData
  );

  const handleCloseModal = () => setOpenModal(false);
  const { token } = useAuth();
  const {
    tokenRate,
    conversionRates,
    userBalance,
    isLoading,
    refetchSettings,
    refetchBalance,
    canPerformAction,
  } = useWalletSettings(token);

  const handleOpenModal = () => {
    refetchBalance();
    refetchSettings();
    setOpenModal(true);
  };
  // ✅ Function to fetch balance with Bearer token
  const fetchUserBalance = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("accessToken"); // or however you store it

      const res = await Axios.get("/wallets/user/wallets", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // ✅ Safely extract token wallet
      if (res.data?.data && Array.isArray(res.data.data)) {
        const tokenWallet = res.data.data.find(
          (wallet: any) => wallet.type === "token"
        );
        if (tokenWallet) {
          // setBalance(tokenWallet.balance);
        }
      }
    } catch (error) {
      console.error("Failed to fetch balance:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Fetch transactions with pagination
  const walletTable = useQuery({
    queryKey: ["walletTable"],
    queryFn: async () => {
      const res = await Axios.get("/wallets/user/transactions", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data;
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
  });

  console.log(walletTable.data);

  // ✅ Call fetch on mount
  // useEffect(() => {
  //   fetchUserBalance();
  // }, [fetchUserBalance]);

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        p: 4,
        borderRadius: "20px",
        width: isMedium ? "100%" : "85%",
        mt: 4,
      }}
    >
      <Stack direction="row" justifyContent={"space-between"}>
        <Typography
          sx={{
            color: "#64748B",
            fontFamily: "Lexend",
            fontWeight: 500,
            fontSize: 16,
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          Token - {loading ? "Loading..." : userBalance?.toFixed(2)} balance
        </Typography>
        <Button variant="contained" size="medium" onClick={handleOpenModal}>
          Add
        </Button>
      </Stack>
      <Divider sx={{ my: 3 }} />

      <TokenTable
        data={walletTable?.data ?? []}
        isLoading={walletTable.isLoading}
      />

      {/* Pagination */}

      <Pagination
        paginationState={paginationState}
        setPaginationState={setPaginationState}
      />

      <Modal open={openModal} onClose={handleCloseModal}>
        <FundingComponent close={handleCloseModal} refetch={refetchBalance} />
      </Modal>
    </Box>
  );
};

export default WalletContent;

function createData(reference: string, amount: string, rate, date: string) {
  return { reference, amount, rate, date };
}

const rows = [
  createData("001", "2000", "20", "1st August 2025, 10:55 AM"),
  createData("002", "5000", "50", "07th August 2025, 2:55 AM"),
  createData("003", "10000", "100", "10th August 2025, 05:55 AM"),
];

const TokenTable = ({
  data,
  isLoading,
}: {
  data: any[];
  isLoading: boolean;
}) => {
  const theme = useTheme();
  const headStyles = {
    ...headerSty,
    color: theme.palette.neutral.main,
    textAlign: "left",
  };
  const contentStyles = {
    ...contentSty,
    textAlign: "left",
    padding: "8px 16px",
  };

  return (
    <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headStyles }}>Reference</TableCell>
            <TableCell sx={{ ...headStyles }}>Amount</TableCell>
            <TableCell sx={{ ...headStyles }}>Rate</TableCell>
            <TableCell sx={{ ...headStyles }}>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((row, id) => (
            <TableRow key={id}>
              <TableCell sx={{ ...contentStyles }}>{row.reference}</TableCell>
              <TableCell sx={{ ...contentStyles }}>{row.amount}</TableCell>
              <TableCell sx={{ ...contentStyles }}>{row.rate}</TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatDateAndTimeFromString(row.created_at)}
              </TableCell>
            </TableRow>
          ))}
          {isLoading && (
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{ ...contentStyles, textAlign: "center" }}
              >
                <Skeleton
                  variant="rectangular"
                  sx={{ my: 1 }}
                  width="100%"
                  height={40}
                />
                <Skeleton
                  variant="rectangular"
                  sx={{ my: 1 }}
                  width="100%"
                  height={40}
                />
                <Skeleton
                  variant="rectangular"
                  sx={{ my: 1 }}
                  width="100%"
                  height={40}
                />
              </TableCell>
            </TableRow>
          )}
          {!isLoading && data?.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{ ...contentStyles, textAlign: "center" }}
              >
                No transactions found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface FundingComponentProps {
  close: () => void;
  refetch: () => void;
}

const FundingComponent: React.FC<FundingComponentProps> = ({
  close,
  refetch,
}) => {
  const [amount, setAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const theme = useTheme();
  const { user, token } = useAuth();
  const { showNotification } = useNotificationService();
  const isMedium = useResponsive("down", "md", "xs");

  const {
    tokenRate,
    conversionRates,
    userBalance,
    isLoading,
    refetchSettings,
    refetchBalance,
    canPerformAction,
  } = useWalletSettings(token);

  // Convert Naira to tokens (100 Naira = 1 Token) tokenRate
  const tokensToReceive = amount
    ? (parseFloat(amount) / Number(tokenRate)).toFixed(2)
    : "0.00";

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers and decimal points
    if (/^\d*\.?\d*$/.test(value) || value === "") {
      setAmount(value);
    }
  };

  // Paystack configuration
  const config = {
    reference: `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    email: user?.email || "",
    amount: Math.round(parseFloat(amount || "0") * 100), // Convert to kobo
    publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY!,
    currency: "NGN",
    metadata: {
      custom_fields: [
        {
          display_name: `${user?.name}`,
          variable_name: "token_purchase",
          value: `${tokensToReceive} tokens`,
          userId: user?._id || "",
          email: user?.email || "",
        },
      ],
    },
  };

  // Initialize payment hook
  const initializePayment = usePaystackPayment(config);

  // Success handler
  const onSuccess = async (reference: any) => {
    console.log("Payment successful:", reference);
    setIsProcessing(true);

    try {
      // Verify payment on backend
      const verified = await verifyPayment(reference.reference);

      if (verified) {
        // get user balance
        refetch();
        close();
      } else {
        showNotification(
          "Payment verification failed. Please contact support with reference: " +
            reference.reference,
          "error"
        );
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      showNotification(
        "Unable to verify payment. Please contact support with reference: " +
          reference.reference
      );
    } finally {
      setIsProcessing(false);
    }
  };

  console.log(tokenRate, conversionRates, userBalance);

  // Close handler
  const onClose = () => {
    console.log("Payment window closed by user");
    setIsProcessing(false);
  };

  // Payment initiation
  const handlePayment = () => {
    if (!amount || parseFloat(amount) <= 0) {
      showNotification("Please enter a valid amount", "error");
      return;
    }

    if (!user?.email) {
      showNotification("User email is required for payment", "error");
      return;
    }

    setIsProcessing(true);
    initializePayment({ onSuccess, onClose });
  };
  // Helper function to verify payment on backend
  const verifyPayment = async (reference: string): Promise<boolean> => {
    try {
      const response = await Axios.post(
        `/wallets/purchase/${reference}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.success === true;
    } catch (error) {
      console.error("Payment verification request failed:", error);
      return false;
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMedium ? "90%" : 400,
    maxWidth: 500,
    bgcolor: "#fff",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
    outline: "none",
  };

  return (
    <Box sx={modalStyle}>
      <Stack spacing={3}>
        {/* Title with token amount */}
        <Box textAlign="center">
          <Typography
            variant="h5"
            sx={{
              fontFamily: "Lexend",
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Fund Wallet
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Lexend",
              fontWeight: 500,
              color: theme.palette.primary.main,
            }}
          >
            {tokensToReceive} Tokens
          </Typography>
        </Box>

        {/* Amount input */}
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Lexend",
              fontWeight: 500,
              color: theme.palette.text.secondary,
              mb: 1,
            }}
          >
            Enter Amount (₦)
          </Typography>
          <TextField
            fullWidth
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            variant="outlined"
            disabled={isProcessing}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 600 }}>₦</Typography>
                </InputAdornment>
              ),
              sx: {
                fontSize: "1.2rem",
                fontFamily: "Lexend",
                borderRadius: "12px",
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />
        </Box>

        {/* Conversion rate */}
        <Box
          sx={{
            backgroundColor: theme.palette.grey[50],
            p: 2,
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Lexend",
              color: theme.palette.text.secondary,
            }}
          >
            Exchange Rate: 1 Token = ₦{tokenRate}
          </Typography>
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            onClick={close}
            disabled={isProcessing}
            sx={{
              borderRadius: "12px",
              fontFamily: "Lexend",
              textTransform: "none",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePayment}
            disabled={!amount || parseFloat(amount) <= 0 || isProcessing}
            sx={{
              borderRadius: "12px",
              fontFamily: "Lexend",
              textTransform: "none",
              px: 3,
              position: "relative",
            }}
          >
            {isProcessing ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{
                    color: "white",
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    marginLeft: "-10px",
                    marginTop: "-10px",
                  }}
                />
                <span style={{ opacity: 0 }}>Processing...</span>
              </>
            ) : (
              `Pay ₦${amount || "0"}`
            )}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
